import "./style.scss";
import { memo, useEffect, useState } from "react";
import CustomSingleSelect from "@/src/components/CustomUtils/CustomSingleSelect/CustomSingleSelect";
import CustomDate from "../../CustomUtils/CustomDate/CustomDate";
import DefaultSelector from "@/src/components/CustomUtils/DefaultSelector/DefaultSelector";
import CustomIncomeManagement from "@/src/components/CustomUtils/CustomIncomeManagement/CustomIncomeManagement";
import CustomDiscount from "@/src/components/CustomUtils/CustomDiscount/CustomDiscount";
import CustomNote from "@/src/components/CustomUtils/CustomNote/CustomNote";
import CustomComments from "@/src/components/CustomUtils/CustomComments/CustomComments";
import { IApplyInfoComment } from "@/src/types/applicationOnline";
import CustomMultiSelect, { CustomMultiSelectItem } from "@/src/components/CustomUtils/CustomMultiSelect/CustomMultiSelect";
import { noteConsts } from "@/src/constants/officeAccountingConsts";
import {
  OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED,
  officeAccountingAdmissionPaymentStatusConst,
  officeAccountingGeneralStatusConst,
  resolveOfficeAccountingAdmissionPaymentStatusId,
  resolveOfficeAccountingAdmissionPaymentStatusLabel,
  resolveOfficeAccountingGeneralStatusId,
  resolveOfficeAccountingGeneralStatusLabel,
} from "@/src/constants/officeAccountingTableConfig";
import { OpenOfficeAccountingFullNameParams } from "../openOfficeAccountingFullNamePopUp";
import DefaultInputChange from "@/src/components/CustomUtils/DefaultInputChange/DefaultInputChange";
import { admissionSemesterOptions } from "@/src/constants/documentPreparationPopUpConsts";
import { buildIntakeOptionsWithStored } from "@/src/utils/intakeCalc";
import { Countries } from "@/src/constants/globalConsts";
import { statusConsts } from "@/src/constants/statusConsts";
import ArrowSvg from "@/src/svg/applicationsPage/APPopUp/Documents/ArrowSvg";
import { SidebarSvgExportConst } from "@/src/svg/sidebar/SidebarSvgExport";

type BodyColumn = {
  id: string;
  value: string;
};

type CellContentProps = {
  column: BodyColumn;
  onChange: (value: string) => void;
  rowId?: string;
  studentInfoId?: number;
  applicantId?: number;
  commentsList?: IApplyInfoComment[];
  onRefreshComments?: () => void | Promise<void>;
  onOpenFullNamePopUp?: (params: OpenOfficeAccountingFullNameParams) => void;
};

const MANAGERS_EMAIL_MOCK = ["admissions@ieu.edu.com", "admissions@ieu.edu.com", "admissions@ieu.edu.com", "admissions@ieu.edu.com"];

const AGENTS_EMAIL_MOCK = ["edugaine@gmail.com", "edugaine@gmail.com", "edugaine@gmail.com", "edugaine@gmail.com", "edugaine@gmail.com"];

const ADMISSION_SEMESTER_OPTIONS = admissionSemesterOptions.map((option) => option.label);

const getIntakeOptions = (storedValue?: string) =>
  buildIntakeOptionsWithStored(storedValue).map((option) => option.label);

const CURRENT_SEMESTER_MOCK = [
  "2st year, 1st semester",
  "2st year, 2nd semester",
  "2st year, 3rd semester",
  "2st year, 4th semester",
  "2st year, 5th semester",
];

const INVOICE_NUMBER_OPTIONS = ["23059-I/01", "23059-I/02", "23059-I/03"];

const INVOICE_NUMBER_MULTI_SELECT_ITEMS: CustomMultiSelectItem[] = INVOICE_NUMBER_OPTIONS.map((number, index) => ({
  id: index + 1,
  title: number,
  cost: "500",
  usd: "USD",
}));

const STATUS_COLUMNS = new Set([
  "statusAl",
  "statusEmail",
  "statusEdbo",
  "statusInvitationFee",
  "balanceStatusUsd",
  "statusMissedClasses",
]);

const CellContent = ({
  column,
  onChange,
  rowId,
  studentInfoId,
  applicantId,
  commentsList = [],
  onRefreshComments,
  onOpenFullNamePopUp,
}: CellContentProps) => {
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [isValidPassportOpen, setIsValidPassportOpen] = useState(false);
  const [isPassportNoOpen, setIsPassportNoOpen] = useState(false);
  const [isPassportIssuedOpen, setIsPassportIssuedOpen] = useState(false);
  const [isAdmissionSemesterOpen, setIsAdmissionSemesterOpen] = useState(false);
  const [isIntakeOpen, setIsIntakeOpen] = useState(false);
  const [isCurrentSemesterOpen, setIsCurrentSemesterOpen] = useState(false);
  const [isManagersEmailOpen, setIsManagersEmailOpen] = useState(false);
  const [isAgentsEmailOpen, setIsAgentsEmailOpen] = useState(false);
  const [isInvitationFeePaidOpen, setIsInvitationFeePaidOpen] = useState(false);
  const [isInvitationFeeOpen, setIsInvitationFeeOpen] = useState(false);
  const [isInvoiceAmountOpen, setIsInvoiceAmountOpen] = useState(false);
  const [isPhoneOpen, setIsPhoneOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isStatusAdmissionPaymentOpen, setIsStatusAdmissionPaymentOpen] = useState(false);
  const [isInvoiceNumberOpen, setIsInvoiceNumberOpen] = useState(false);
  const [isGeneralStatusOpen, setIsGeneralStatusOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [countryList, setCountryList] = useState<string[]>(Countries);
  const [managersEmail, setManagersEmail] = useState<string[]>(MANAGERS_EMAIL_MOCK);
  const [agentsEmail, setAgentsEmail] = useState<string[]>(AGENTS_EMAIL_MOCK);
  const [currentSemester, setCurrentSemester] = useState<string[]>(CURRENT_SEMESTER_MOCK);
  const [isStatusNostrificationOpen, setIsStatusNostrificationOpen] = useState(false);
  const [isPreparatoryDepartmentOpen, setIsPreparatoryDepartmentOpen] = useState(false);

  const handleOpenFullNamePopUp = () => {
    if (!applicantId || !studentInfoId) return;
    onOpenFullNamePopUp?.({ applicantId, studentInfoId });
  };

  useEffect(() => {
    if (column.id !== "generalStatus" || !isGeneralStatusOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".general-status")) {
        setIsGeneralStatusOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [column.id, isGeneralStatusOpen]);

  useEffect(() => {
    if (column.id !== "statusAdmissionPayment" || !isStatusAdmissionPaymentOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".status-admission-payment")) {
        setIsStatusAdmissionPaymentOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [column.id, isStatusAdmissionPaymentOpen]);

  const handleGeneralStatusSelect = (label: string) => {
    onChange(label === OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED ? "" : label);
    setIsGeneralStatusOpen(false);
  };

  // FULL NAME
  if (column.id === "fullName") {
    return (
      <div className="cell-content full-name clicable" onClick={handleOpenFullNamePopUp}>
        <div className="full-name-cell">
          <p>{column.value}</p>
          <ArrowSvg />
        </div>
      </div>
    );
  }

  // COUNTRY
  if (column.id === "country") {
    return (
      <div className="cell-content country">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsCountryOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isCountryOpen && (
          <CustomSingleSelect
            title={"Choose country"}
            data={countryList}
            isSearch={true}
            allowAdd={false}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsCountryOpen(false)}
            onAddItem={(value) => setCountryList((prev) => [...prev, value])}
          />
        )}
      </div>
    );
  }
  // COMMENTS
  if (column.id === "comments") {
    return (
      <div className="cell-content cell-content-comments">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsCommentsOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isCommentsOpen && studentInfoId != null && studentInfoId > 0 && (
          <CustomComments
            title="Comments"
            comments={commentsList}
            accountingId={studentInfoId}
            onRefresh={onRefreshComments}
            onClose={() => setIsCommentsOpen(false)}
          />
        )}
      </div>
    );
  }
  // DATE OF BIRTH
  if (column.id === "dateOfBirth") {
    return (
      <div className="cell-content date">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsDateOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isDateOpen && <CustomDate value={column.value} onChange={onChange} onClose={() => setIsDateOpen(false)} />}
      </div>
    );
  }
  // PASSPORT NO
  if (column.id === "passportNo") {
    return (
      <div className="cell-content passport-no">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsPassportNoOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isPassportNoOpen && (
          <DefaultInputChange
            title={"Enter passport number"}
            value={column.value}
            onChange={onChange}
            onClose={() => setIsPassportNoOpen(false)}
          />
        )}
      </div>
    );
  }
  // PASSPORT ISSUED DATE
  if (column.id === "passportIssued") {
    return (
      <div className="cell-content passport-issued">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsPassportIssuedOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isPassportIssuedOpen && <CustomDate value={column.value} onChange={onChange} onClose={() => setIsPassportIssuedOpen(false)} />}
      </div>
    );
  }
  // VALID PASSPORT DATE
  if (column.id === "validPassport") {
    return (
      <div className="cell-content valid-passport">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsValidPassportOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isValidPassportOpen && <CustomDate value={column.value} onChange={onChange} onClose={() => setIsValidPassportOpen(false)} />}
      </div>
    );
  }
  // PHONE
  if (column.id === "phone") {
    return (
      <div className="cell-content phone">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsPhoneOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isPhoneOpen && (
          <DefaultInputChange title={"Enter phone number"} value={column.value} onChange={onChange} onClose={() => setIsPhoneOpen(false)} />
        )}
      </div>
    );
  }
  // EMAIL
  if (column.id === "email") {
    return (
      <div className="cell-content email">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsEmailOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isEmailOpen && (
          <DefaultInputChange title={"Enter email"} value={column.value} onChange={onChange} onClose={() => setIsEmailOpen(false)} />
        )}
      </div>
    );
  }
  // MANAGERS EMAIL
  if (column.id === "managersEmail") {
    return (
      <div className="cell-content managers-email">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsManagersEmailOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isManagersEmailOpen && (
          <CustomSingleSelect
            title={"Choose manager email"}
            data={managersEmail}
            isSearch={true}
            allowAdd={true}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsManagersEmailOpen(false)}
            onAddItem={(value) => setManagersEmail((prev) => [...prev, value])}
          />
        )}
      </div>
    );
  }
  // AGENTS EMAIL
  if (column.id === "agentsEmail") {
    return (
      <div className="cell-content agents-email">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsAgentsEmailOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isAgentsEmailOpen && (
          <CustomSingleSelect
            title={"Choose agent email"}
            data={agentsEmail}
            isSearch={true}
            allowAdd={true}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsAgentsEmailOpen(false)}
            onAddItem={(value) => setAgentsEmail((prev) => [...prev, value])}
          />
        )}
      </div>
    );
  }
  // ADMISSION SEMESTER
  if (column.id === "admissionSemester") {
    return (
      <div className="cell-content admission-semester">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsAdmissionSemesterOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isAdmissionSemesterOpen && (
          <CustomSingleSelect
            title={"Choose admission semester"}
            data={ADMISSION_SEMESTER_OPTIONS}
            isSearch={true}
            allowAdd={false}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsAdmissionSemesterOpen(false)}
          />
        )}
      </div>
    );
  }
  // INTAKE
  if (column.id === "intake") {
    return (
      <div className="cell-content intake">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsIntakeOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isIntakeOpen && (
          <CustomSingleSelect
            title={"Choose intake"}
            data={getIntakeOptions(column.value)}
            isSearch={true}
            allowAdd={false}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsIntakeOpen(false)}
          />
        )}
      </div>
    );
  }
  // CURRENT SEMESTER
  if (column.id === "currentSemester") {
    return (
      <div className="cell-content current-semester">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsCurrentSemesterOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isCurrentSemesterOpen && (
          <CustomSingleSelect
            title={"Choose current semester"}
            data={currentSemester}
            isSearch={false}
            allowAdd={true}
            columnValue={column.value}
            onChange={onChange}
            onClose={() => setIsCurrentSemesterOpen(false)}
            onAddItem={(value) => setCurrentSemester((prev) => [...prev, value])}
          />
        )}
      </div>
    );
  }
  // STATUS (general_status) — same badge + menu pattern as Online applications
  if (column.id === "generalStatus") {
    const statusId = resolveOfficeAccountingGeneralStatusId(column.value);
    const statusLabel = resolveOfficeAccountingGeneralStatusLabel(column.value);

    return (
      <div className="cell-content general-status">
        <div
          className={`status-option ${statusId}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsGeneralStatusOpen((prev) => !prev);
          }}
        >
          <span className="status-label">{statusLabel}</span>
        </div>
        {isGeneralStatusOpen && (
          <div className="status-menu">
            {officeAccountingGeneralStatusConst.map(({ id, label }) => (
              <div
                key={id}
                className={`status-option ${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGeneralStatusSelect(label);
                }}
              >
                <span className="status-label">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  // STATUS ADMISSION PAYMENT
  if (column.id === "statusAdmissionPayment") {
    const statusId = resolveOfficeAccountingAdmissionPaymentStatusId(column.value);
    const statusLabel = resolveOfficeAccountingAdmissionPaymentStatusLabel(column.value);

    return (
      <div className="cell-content status-admission-payment">
        <div
          className={`status-option ${statusId}`}
          onClick={(e) => {
            e.stopPropagation();
            setIsStatusAdmissionPaymentOpen((prev) => !prev);
          }}
        >
          <span className="status-label">{statusLabel}</span>
        </div>
        {isStatusAdmissionPaymentOpen && (
          <div className="status-menu">
            {officeAccountingAdmissionPaymentStatusConst.map(({ id, label }) => (
              <div
                key={id}
                className={`status-option ${id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(label);
                  setIsStatusAdmissionPaymentOpen(false);
                }}
              >
                <span className="status-label">{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  // INVOICE №
  if (column.id === "invoiceNumber") {
    return (
      <div className="cell-content invoice-number">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsInvoiceNumberOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isInvoiceNumberOpen && (
          <CustomMultiSelect
            title={"Choose invoice №"}
            items={INVOICE_NUMBER_MULTI_SELECT_ITEMS}
            isSearch={true}
            initialSelectedTitles={column.value}
            onClose={() => setIsInvoiceNumberOpen(false)}
            onApply={(selected) => onChange(selected.map((item) => item.title).join(", "))}
          />
        )}
      </div>
    );
  }
  // STATUS POPUP (PAYMENTS / EMAIL / EDBO / BALANCES)
  if (STATUS_COLUMNS.has(column.id)) {
    const statusId =
      statusConsts.find((status) => status.label.toLowerCase() === column.value.toLowerCase() || status.id === column.value)?.id ?? "";

    return (
      <div className="cell-content payments">
        <p className={statusId} onClick={() => setIsStatusOpen(true)}>
          {column.value}
        </p>
        {isStatusOpen && (
          <DefaultSelector value={column.value} options={statusConsts} onChange={onChange} onClose={() => setIsStatusOpen(false)} />
        )}
      </div>
    );
  }
  // INVITATION FEE
  if (column.id === "invitationFee") {
    return (
      <div className="cell-content invitation-fee">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsInvitationFeeOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isInvitationFeeOpen && <CustomIncomeManagement title={"Income Management in USD"} onClose={() => setIsInvitationFeeOpen(false)} />}
      </div>
    );
  }
  // INVITATION FEE PAID
  if (column.id === "invitationFeePaid") {
    return (
      <div className="cell-content invitation-fee-paid">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsInvitationFeePaidOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isInvitationFeePaidOpen && (
          <CustomIncomeManagement title={"Income Management in USD"} onClose={() => setIsInvitationFeePaidOpen(false)} />
        )}
      </div>
    );
  }
  // INVOICE AMOUNT
  if (column.id === "invoiceAmount") {
    return (
      <div className="cell-content invoice-amount">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsInvoiceAmountOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isInvoiceAmountOpen && <CustomIncomeManagement title={"Income Management in USD"} onClose={() => setIsInvoiceAmountOpen(false)} />}
      </div>
    );
  }
  // STATUS NOSTRIFICATION
  if (column.id === "statusNostrification") {
    return (
      <div className="cell-content status-nostrification">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsStatusNostrificationOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isStatusNostrificationOpen && <CustomDiscount title={"Select an option"} onClose={() => setIsStatusNostrificationOpen(false)} />}
      </div>
    );
  }
  // PREPARATORY DEPARTMENT
  if (column.id === "preparatoryDepartment") {
    const noteMatchId = rowId || (studentInfoId != null ? String(studentInfoId) : "");
    const noteConst = noteConsts.find((item) => item.id === noteMatchId);
    return (
      <div className="cell-content preparatory-department">
        <p>{column.value}</p>
        <div className="cell-content-edit" onClick={() => setIsPreparatoryDepartmentOpen(true)}>
          <SidebarSvgExportConst.CustomizationSvg />
        </div>
        {isPreparatoryDepartmentOpen && (
          <CustomNote note={noteConst?.note ?? noteConsts[0]?.note ?? []} onClose={() => setIsPreparatoryDepartmentOpen(false)} />
        )}
      </div>
    );
  }
  /////////////////////////////
  // DISCOUNT
  // if (column.id === "discount") {
  //   return (
  //     <div className="cell-content discount">
  //       <p>{column.value}</p>
  //       <div className="cell-content-edit" onClick={() => setIsDiscountOpen(true)}>
  //         <SidebarSvgExportConst.CustomizationSvg />
  //       </div>
  //       {isDiscountOpen && <CustomDiscount title={"Select an option"} onClose={() => setIsDiscountOpen(false)} />}
  //     </div>
  //   );
  // }
  // // NOTE ADJUSTMENT
  // if (column.id === "noteAdjustment") {
  //   const noteMatchId = rowId ?? column.value;
  //   const noteConst = noteConsts.find((item) => item.id === noteMatchId);
  //   if (noteConst) {
  //     return (
  //       <div className="cell-content note">
  //         <p>{column.value}</p>
  //         <div className="cell-content-edit" onClick={() => setIsNoteOpen(true)}>
  //           <SidebarSvgExportConst.CustomizationSvg />
  //         </div>
  //         {isNoteOpen && <CustomNote note={noteConst.note} onClose={() => setIsNoteOpen(false)} />}
  //       </div>
  //     );
  //   }
  //   return <p>{column.value}</p>;
  // }

  return <p>{column.value}</p>;
};

export default memo(CellContent, (prev, next) => {
  return (
    prev.column.id === next.column.id &&
    prev.column.value === next.column.value &&
    prev.rowId === next.rowId &&
    prev.studentInfoId === next.studentInfoId &&
    prev.applicantId === next.applicantId &&
    prev.commentsList === next.commentsList &&
    prev.onRefreshComments === next.onRefreshComments &&
    prev.onOpenFullNamePopUp === next.onOpenFullNamePopUp &&
    prev.onChange === next.onChange
  );
});
